/**
 * Resolve a Space's logo or banner to something an `<img>` can show.
 *
 * Cleartext Spaces return the CDN URL. `encryptIdentity` Spaces decrypt the
 * pointer with the Space key, then fetch + decrypt the E2E blob through the
 * shared `useE2EMediaDownload` cache (one download per blob per session).
 *
 * Scale: after a `space_updated`, every online member's sidebar would fetch
 * the new image at once. Encrypted images are fetched only while `visible`
 * (callers pass an on-screen signal), and in a background tab only after a
 * random delay, so a logo change does not produce a synchronized burst.
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import type { PublicSpace } from '@adieuu/shared';
import { peekSpaceKey } from '../../services/spaceCipherService';
import { evictE2EMediaCacheEntry, useE2EMediaDownload } from '../../hooks/useE2EMediaDownload';
import type { MediaAttachment } from '../../services/messagePayload';
import { useSpaceCipher } from './useSpaceCipher';
import { decryptSpaceMediaPointer } from './spaceMetadataCipher';

export type SpaceIdentityMediaState =
  | 'none'
  | 'loading'
  | 'locked'
  | 'scanning'
  | 'available'
  | 'rejected'
  | 'error';

export interface SpaceIdentityMediaResult {
  url: string | null;
  state: SpaceIdentityMediaState;
}

export type SpaceIdentityMediaSpace = Pick<
  PublicSpace,
  | 'id'
  | 'encryptIdentity'
  | 'cipherId'
  | 'logoUrl'
  | 'bannerUrl'
  | 'encryptedLogo'
  | 'logoNonce'
  | 'encryptedBanner'
  | 'bannerNonce'
>;

export interface UseSpaceIdentityMediaOptions {
  /** Whether the image is on screen. Encrypted images are not fetched while false. */
  visible?: boolean;
  /** Upper bound of the random delay before fetching in a background tab. */
  backgroundJitterMs?: number;
}

export const SPACE_MEDIA_BACKGROUND_JITTER_MS = 30_000;

const EMPTY_ATTACHMENT: MediaAttachment = {
  e2eMediaId: '',
  scanHash: '',
  contentType: 'image/webp',
  exifPreserved: false,
  encryptionKey: '',
  encryptionNonce: '',
};

function isDocumentHidden(): boolean {
  return typeof document !== 'undefined' && document.visibilityState === 'hidden';
}

/**
 * True once an encrypted image may be fetched: on screen, and either the tab
 * is in the foreground or a random background delay has elapsed.
 */
function useFetchGate(visible: boolean, jitterMs: number, key: string | null): boolean {
  const [allowed, setAllowed] = useState(() => visible && !isDocumentHidden());

  useEffect(() => {
    if (!visible || !key) {
      setAllowed(false);
      return;
    }
    if (!isDocumentHidden()) {
      setAllowed(true);
      return;
    }
    setAllowed(false);
    const timer = setTimeout(() => setAllowed(true), Math.floor(Math.random() * jitterMs));
    const onVisible = () => {
      if (!isDocumentHidden()) {
        clearTimeout(timer);
        setAllowed(true);
      }
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      clearTimeout(timer);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [visible, jitterMs, key]);

  return allowed;
}

export function useSpaceIdentityMedia(
  space: SpaceIdentityMediaSpace | null | undefined,
  slot: 'logo' | 'banner',
  options: UseSpaceIdentityMediaOptions = {},
): SpaceIdentityMediaResult {
  const visible = options.visible ?? true;
  const jitterMs = options.backgroundJitterMs ?? SPACE_MEDIA_BACKGROUND_JITTER_MS;
  const encrypted = !!space?.encryptIdentity;
  const { spaceCipher } = useSpaceCipher(encrypted ? space?.id : null);

  const hasCiphertext = !!(slot === 'logo' ? space?.encryptedLogo : space?.encryptedBanner);
  const spaceKey = encrypted && spaceCipher && space ? peekSpaceKey(spaceCipher, space.id) : null;

  const pointer = useMemo(() => {
    if (!encrypted || !space || !hasCiphertext || !spaceCipher || !spaceKey) return null;
    return decryptSpaceMediaPointer(spaceCipher, space, slot, spaceKey);
  }, [encrypted, space, slot, hasCiphertext, spaceCipher, spaceKey]);

  // A replaced image is never shown again; release its decrypted blob.
  const previousIdRef = useRef<string | null>(null);
  useEffect(() => {
    const current = pointer?.e2eMediaId ?? null;
    const previous = previousIdRef.current;
    if (previous && previous !== current) evictE2EMediaCacheEntry(previous);
    previousIdRef.current = current;
  }, [pointer?.e2eMediaId]);

  const allowed = useFetchGate(visible, jitterMs, pointer?.e2eMediaId ?? null);

  const attachment = useMemo<MediaAttachment>(
    () =>
      pointer
        ? {
            e2eMediaId: pointer.e2eMediaId,
            scanHash: '',
            contentType: pointer.contentType,
            exifPreserved: false,
            encryptionKey: pointer.key,
            encryptionNonce: pointer.nonce,
            ...(pointer.width ? { width: pointer.width } : {}),
            ...(pointer.height ? { height: pointer.height } : {}),
          }
        : EMPTY_ATTACHMENT,
    [pointer],
  );
  const download = useE2EMediaDownload(attachment, { skip: !pointer || !allowed });

  if (!space) return { url: null, state: 'none' };

  if (!encrypted) {
    const url = slot === 'logo' ? space.logoUrl : space.bannerUrl;
    return url ? { url, state: 'available' } : { url: null, state: 'none' };
  }

  if (!hasCiphertext) return { url: null, state: 'none' };
  if (!spaceCipher) return { url: null, state: 'locked' };
  if (!spaceKey) return { url: null, state: 'loading' };
  if (!pointer) return { url: null, state: 'error' };
  if (!allowed) return { url: null, state: 'loading' };

  switch (download.state) {
    case 'available':
      return download.imageUrl ? { url: download.imageUrl, state: 'available' } : { url: null, state: 'loading' };
    case 'scanning':
      return { url: null, state: 'scanning' };
    case 'rejected':
      return { url: null, state: 'rejected' };
    case 'error':
      return { url: null, state: 'error' };
    default:
      return { url: null, state: 'loading' };
  }
}
